/**
 * The price a line inherits when only some of its project's cities are picked.
 *
 * Owner's ruling, 2026-10-18: the catalogue `unit_price` is the FULL-TOUR rate,
 * so a line selling some of a project's cities is priced per city —
 * `unit_price × selected ÷ N`, where N is the number of cities on the line's
 * project. BSF India Summer 2026 at 17,500 over 5 cities is 3,500 a city.
 *
 * ⚠️ **This mirrors the server's `src/lib/city-pro-rata.ts`; the server's stored
 * price always wins.** It exists only so a grid can show the inherited price
 * before the save. A grid never SENDS this figure as `unitPrice` — leaving the
 * key out is what lets the server apply the same rule.
 *
 * ⚠️ **The money goes on the UNIT PRICE, never the quantity.** Quantity is not
 * derived from cities, and a line total is never money × city count.
 *
 * ⚠️ **Multiply first, divide once.** 10,000 over 3 cities, 2 picked, is
 * 6,666.67 — rounding the per-city rate first would give 6,666.66. Exact BigInt
 * arithmetic through `lib/decimal.ts`, rounding half away from zero; no `Number`
 * ever touches the amount.
 *
 * The full catalogue price comes back unchanged when there is nothing to
 * pro-rate: the line covers all the project's cities, the project has no cities,
 * no city is picked, or as many as the project has (or more) are picked.
 * `null` (no catalogue price) stays `null`.
 */

import { splitAmountOverQuantity, sumDecimalStrings } from '@/lib/decimal';

export function proRataUnitPrice(input: {
  catalogUnitPrice: string | null;
  projectCityCount: number;
  selectedCityCount: number;
  allProjectCities?: boolean;
}): string | null {
  const { catalogUnitPrice, projectCityCount, selectedCityCount, allProjectCities } = input;
  if (catalogUnitPrice === null) return null;
  if (allProjectCities === true) return catalogUnitPrice;
  if (!Number.isInteger(projectCityCount) || !Number.isInteger(selectedCityCount)) {
    return catalogUnitPrice;
  }
  if (projectCityCount <= 0 || selectedCityCount <= 0) return catalogUnitPrice;
  if (selectedCityCount >= projectCityCount) return catalogUnitPrice;

  // price × selected, exactly: a sum of `selected` copies (a city count is small).
  const gross = sumDecimalStrings(Array.from({ length: selectedCityCount }, () => catalogUnitPrice));
  if (gross === null) return catalogUnitPrice;

  // One round-half division to cents. A price this cannot read is left as-is.
  const split = splitAmountOverQuantity(gross, projectCityCount);
  return split === null ? catalogUnitPrice : split.unitPrice;
}
